import React, { useState } from 'react';
import {
  Card,
  Input,
  Button,
  Form,
  message,
  Spin,
  Descriptions,
  Tag,
  Space,
  Alert,
  Divider,
  Row,
  Col,
  Avatar,
  Image,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  UserOutlined,
  GiftOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import apiClient from '../services/apiConfig';

const { TextArea } = Input;

const BASE_IMAGE_URL_PROMOTIONS = 'https://chiavari.sbcode.com.ar/api/public/storage/promotions/';
const BASE_IMAGE_URL_REWARDS = 'https://chiavari.sbcode.com.ar/api/public/storage/rewards/';

interface Redemption {
  id: number;
  redemption_code: string;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  redeemed_at?: string;
  points_used?: number;
  user?: {
    id: number;
    name: string;
    last_name?: string;
    email: string;
    category?: string;
  };
  promotion?: {
    title: string;
    description: string;
    main_image?: string;
  };
  reward?: {
    name: string;
    description: string;
    points_cost: number;
    image?: string;
  };
  notes?: string;
  type: 'promotion' | 'reward';
}

const RedemptionValidationPage: React.FC = () => {
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [redemption, setRedemption] = useState<Redemption | null>(null);
  const [notes, setNotes] = useState('');
  const [form] = Form.useForm();

  const handleValidateCode = async () => {
    if (!code.trim()) {
      message.warning('Por favor ingresa un código de canje');
      return;
    }

    try {
      setValidating(true);
      setRedemption(null);

      // Intentar primero con rewards
      try {
        const response = await apiClient.get(`/rewards/redemptions/validate/${code.trim()}`);
        if (response.data.success) {
          setRedemption({ ...response.data.data, type: 'reward' });
          message.success('Código de premio validado correctamente');
          return;
        }
      } catch (rewardError) {
        // Si falla, intentar con promociones
        try {
          const response = await apiClient.get(`/promotion-redemptions/validate/${code.trim()}`);
          if (response.data.success) {
            setRedemption({ ...response.data.data, type: 'promotion' });
            message.success('Código de promoción validado correctamente');
            return;
          }
        } catch (promotionError) {
          throw new Error('Código inválido o no encontrado');
        }
      }
    } catch (error: any) {
      console.error('Error validating code:', error);
      setRedemption(null);
      const errorMessage = error.response?.data?.message || error.message || 'Código inválido o no encontrado';
      message.error(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  const handleConfirmRedemption = async () => {
    if (!redemption) return;

    try {
      setConfirming(true);
      const endpoint = redemption.type === 'reward'
        ? `/rewards/redemptions/confirm/${redemption.redemption_code}`
        : `/promotion-redemptions/confirm/${redemption.redemption_code}`;

      await apiClient.post(endpoint, notes ? { notes } : {});
      message.success('Canje confirmado exitosamente');

      setCode('');
      setRedemption(null);
      setNotes('');
      form.resetFields();
    } catch (error: any) {
      console.error('Error confirming redemption:', error);
      const errorMessage = error.response?.data?.message || 'No se pudo confirmar el canje';
      message.error(errorMessage);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelRedemption = async () => {
    if (!redemption) return;

    try {
      setCancelling(true);
      const endpoint = redemption.type === 'reward'
        ? `/rewards/redemptions/cancel/${redemption.redemption_code}`
        : `/promotion-redemptions/cancel/${redemption.redemption_code}`;

      await apiClient.post(endpoint);
      message.success('Canje cancelado exitosamente. Puntos devueltos.');

      setCode('');
      setRedemption(null);
      setNotes('');
      form.resetFields();
    } catch (error: any) {
      console.error('Error cancelling redemption:', error);
      const errorMessage = error.response?.data?.message || 'No se pudo cancelar el canje';
      message.error(errorMessage);
    } finally {
      setCancelling(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pendiente', icon: <CloseCircleOutlined /> },
      completed: { color: 'success', text: 'Completado', icon: <CheckCircleOutlined /> },
      cancelled: { color: 'error', text: 'Cancelado', icon: <CloseCircleOutlined /> },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getItemImage = () => {
    if (redemption?.type === 'reward' && redemption.reward?.image) {
      return `${BASE_IMAGE_URL_REWARDS}${redemption.reward.image}`;
    }
    if (redemption?.type === 'promotion' && redemption.promotion?.main_image) {
      return `${BASE_IMAGE_URL_PROMOTIONS}${redemption.promotion.main_image}`;
    }
    return null;
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ fontSize: '28px', marginBottom: '24px', fontWeight: 600 }}>
        Validación de Canjes
      </h1>

      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <Card
            title="Escanear o ingresar código"
            extra={<SearchOutlined />}
            style={{ marginBottom: '24px' }}
          >
            <Form form={form} layout="vertical">
              <Form.Item
                label="Código de Canje"
                name="code"
                help="Ingresa el código (promoción o premio)"
              >
                <Input
                  size="large"
                  placeholder="RWD-XXXXX o CHV-XXXX"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onPressEnter={handleValidateCode}
                  disabled={validating || confirming || cancelling}
                  maxLength={20}
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>

              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handleValidateCode}
                loading={validating}
                disabled={!code.trim() || confirming || cancelling}
                block
              >
                {validating ? 'Validando...' : 'Validar Código'}
              </Button>
            </Form>

            {redemption && (
              <Alert
                message={redemption.type === 'reward' ? 'Código de Premio Válido' : 'Código de Promoción Válido'}
                description={`Este código corresponde a un canje ${redemption.status === 'pending' ? 'pendiente' : redemption.status}.`}
                type={redemption.status === 'pending' ? 'success' : 'warning'}
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Instrucciones" style={{ marginBottom: '24px' }}>
            <ol style={{ paddingLeft: '20px', lineHeight: '28px' }}>
              <li>Solicita al usuario que te muestre su código de canje</li>
              <li>Ingresa o escanea el código en el campo de arriba</li>
              <li>Verifica los detalles del canje (usuario, item, puntos)</li>
              <li>Opcionalmente agrega notas sobre el canje</li>
              <li>Presiona "Confirmar Canje" para completar</li>
            </ol>
            <Alert
              message="Importante"
              description="Al confirmar, el estado cambiará a 'completado'. Al cancelar, se devolverán los puntos al usuario."
              type="info"
              showIcon
              style={{ marginTop: '16px' }}
            />
          </Card>
        </Col>
      </Row>

      {redemption && (
        <Card
          title="Detalles del Canje"
          style={{ marginBottom: '24px' }}
          extra={
            <Space>
              <Tag color={redemption.type === 'reward' ? 'purple' : 'blue'}>
                {redemption.type === 'reward' ? 'Premio Interno' : 'Promoción'}
              </Tag>
              {getStatusTag(redemption.status)}
            </Space>
          }
        >
          {getItemImage() && (
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <Image src={getItemImage()!} alt="item" width={200} style={{ borderRadius: 8 }} />
            </div>
          )}

          <Descriptions bordered column={2}>
            <Descriptions.Item label="Código" span={2}>
              <strong style={{ fontSize: '18px', letterSpacing: '2px' }}>
                {redemption.redemption_code}
              </strong>
            </Descriptions.Item>

            <Descriptions.Item label="Estado" span={2}>
              {getStatusTag(redemption.status)}
            </Descriptions.Item>

            <Descriptions.Item
              label={<Space><UserOutlined /><span>Usuario</span></Space>}
              span={1}
            >
              <Space>
                <Avatar icon={<UserOutlined />} />
                <div>
                  <div>{redemption.user?.name || 'Usuario'} {redemption.user?.last_name || ''}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {redemption.user?.email || ''}
                  </div>
                  {redemption.user?.category && (
                    <Tag style={{ marginTop: 4 }}>{redemption.user.category.toUpperCase()}</Tag>
                  )}
                </div>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label="Fecha de solicitud" span={1}>
              {formatDate(redemption.created_at)}
            </Descriptions.Item>

            <Descriptions.Item
              label={<Space><GiftOutlined /><span>{redemption.type === 'reward' ? 'Premio' : 'Promoción'}</span></Space>}
              span={2}
            >
              <strong>
                {redemption.type === 'reward' ? redemption.reward?.name : redemption.promotion?.title}
              </strong>
              <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                {redemption.type === 'reward' ? redemption.reward?.description : redemption.promotion?.description}
              </div>
            </Descriptions.Item>

            {redemption.points_used !== undefined && (
              <Descriptions.Item
                label={<Space><TrophyOutlined /><span>Puntos</span></Space>}
                span={2}
              >
                <Tag color="blue" style={{ fontSize: '16px', padding: '4px 12px' }}>
                  {redemption.points_used} puntos
                </Tag>
              </Descriptions.Item>
            )}

            {redemption.notes && (
              <Descriptions.Item label="Notas" span={2}>
                {redemption.notes}
              </Descriptions.Item>
            )}

            {redemption.redeemed_at && (
              <Descriptions.Item label="Canjeado el" span={2}>
                {formatDate(redemption.redeemed_at)}
              </Descriptions.Item>
            )}
          </Descriptions>

          {redemption.status === 'pending' && (
            <>
              <Divider />

              <Form layout="vertical">
                <Form.Item
                  label="Notas (Opcional)"
                  help="Agrega cualquier observación sobre este canje"
                >
                  <TextArea
                    rows={4}
                    placeholder="Ej: Cliente atendido en recepción..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={500}
                    showCount
                    disabled={confirming || cancelling}
                  />
                </Form.Item>

                <Space style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleConfirmRedemption}
                    loading={confirming}
                    style={{ background: '#52c41a', borderColor: '#52c41a', flex: 1 }}
                  >
                    {confirming ? 'Confirmando...' : 'Confirmar Canje'}
                  </Button>

                  <Button
                    danger
                    size="large"
                    icon={<CloseCircleOutlined />}
                    onClick={handleCancelRedemption}
                    loading={cancelling}
                    disabled={confirming}
                  >
                    {cancelling ? 'Cancelando...' : 'Cancelar y Devolver Puntos'}
                  </Button>
                </Space>
              </Form>
            </>
          )}

          {redemption.status === 'completed' && (
            <Alert
              message="Canje ya completado"
              description="Este canje ya fue confirmado anteriormente."
              type="info"
              showIcon
              style={{ marginTop: '16px' }}
            />
          )}

          {redemption.status === 'cancelled' && (
            <Alert
              message="Canje cancelado"
              description="Este canje fue cancelado y no puede ser procesado."
              type="warning"
              showIcon
              style={{ marginTop: '16px' }}
            />
          )}
        </Card>
      )}

      {validating && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#888' }}>Validando código...</p>
        </div>
      )}
    </div>
  );
};

export default RedemptionValidationPage;
